import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Charge } from '../../../../models/entities/charge';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { ChargesService } from '../../../../services/charges/charges.service';
import { MatLegacyDialog as MatDialog, MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput, ConfirmActionDialogResult } from '../confirm-action-dialog/confirm-action-dialog.component';
import { GenericDialogResult } from '../generic-mat-dialog/generic-dialog-result';
import { ArchiveService } from '../../../../services/archive.service';
import moment from 'moment-timezone';
import { ValidationMessageService } from '../../../../services/validation-message.service';
import { CommunicationService } from '../../../../services/communication/communication.service';
import { Router } from '@angular/router';
import { ConfirmWithInputDialogComponent, ConfirmWithInputDialogInput, ConfirmWithInputDialogResult } from '../confirm-with-input-dialog/confirm-with-input-dialog.component';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { OrganizationService } from '../../../../services/organization/organization.service';
import { Organization } from '../../../../models/entities/organization';
import { BulkChargeEditorComponent, BulkChargeEditorDialogInput, BulkChargeEditorDialogOutput } from '../../charges/bulk-charge-editor/bulk-charge-editor.component';
import { SelectionModel } from '@angular/cdk/collections';
import { SharedModule } from '../../../shared/shared.module';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { LoadingOverlayComponent } from '../loading-overlay/loading-overlay.component';
import { PaymentPlanDialogComponent } from '../payment-plan-dialog/payment-plan-dialog.component';

/**
 * Bulk operations menu for managing multiple charges (invoices) with state-aware action filtering.
 *
 * Charge lifecycle constraints: Active (unpaid) charges can be forgiven/deleted/emailed. Deleted charges
 * can only be reinstated or permanently archived. Paid charges are excluded from most operations except
 * editing. Account credits are automatically issued when deleting charges with partial payments.
 *
 * Autopay email suppression: When org preference `sendMonthlyStatementsToAutopayMembers` is false,
 * invoice emails skip autopay-enabled charges unless explicitly overridden via confirmation dialog.
 */
@Component({
    selector: 'lf-multi-charge-options',
    templateUrl: './multi-charge-options.component.html',
    standalone: true,
    imports: [
        SharedModule,
        MatTooltipModule,
        MatMenuModule,
        MatDialogModule,
        LoadingOverlayComponent,
    ],
})
export class MultiChargeOptionsComponent implements OnInit
{
    /** Charges that are not deleted and not paid */
    activeCharges: Charge[] = [];
    /** Ids of the above ^^ charges (not deleted, not paid) */
    activeIds: number[] = [];
    /** Ids of charges that are not deleted; may be paid, may not */
    liveCharges: Charge[] = [];
    /** Ids of charges that are deleted */
    deletedIds: number[] = [];
    /** Ids of charges that are not deleted, not paid, and have late fees */
    lateFeeIds: number[] = [];
    /** Charges that are paid */
    paidCharges: Charge[] = [];
    /** Ids of payors that have charges that are not deleted and not paid */
    payorIds: Set<number> = new Set<number>();
    /** Ids of charges that are not deleted, not paid, have an earlyBefore date, and an early discount */
    earlyDiscountIds: number[] = [];

    /** If the number of payor types + id (as a key) is just 1, for all charges sent in */
    isSinglePayor = false;

    @Input() charges: SelectionModel<Charge>;
    @Input() itemsOnly = false;

    @Output() reloadCharges: EventEmitter<void> = new EventEmitter<void>();
    @Output() exportCsv: EventEmitter<void> = new EventEmitter<void>();

    jwt: JwtLegFiClaims;

    hasPaymentWriteAccess = false;
    hasChargeWriteAccess = false;
    hasChargeReadAccess = false;
    hasCommunicationReadAccess = false;
    doShowLoadingOverlay = false;

    org: Organization = null;

    constructor(
            private _archiveService: ArchiveService,
            private _chargesService: ChargesService,
            private _communicationService: CommunicationService,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
            private _router: Router,
            private _validationMessageService: ValidationMessageService,
            private _organizationService: OrganizationService,
    ) {
    }

    ngOnInit() {
        this.hasPaymentWriteAccess = LegFiJwtService.doesUserHaveModulePermission('payments', true);
        this.hasChargeWriteAccess = LegFiJwtService.doesUserHaveModulePermission('charges', true);
        this.hasChargeReadAccess = LegFiJwtService.doesUserHaveModulePermission('charges', false);
        this.hasCommunicationReadAccess = LegFiJwtService.doesUserHaveModulePermission('communication', false);
        this.jwt = LegFiJwtService.read();

        this._organizationService.getOrganizationById(this.jwt.orgId, ['preferences']).subscribe(({
            next: (response: Organization) => {
                this.org = response;
            },
            error: () => {
                this._growler.oops('There was an error gathering organization information.');
            },
        }));

        this.charges.changed.subscribe(() => {
            this.liveCharges = this.charges.selected.filter((charge) => {
                return !charge.deletedAt;
            });

            this.activeCharges = this.charges.selected.filter((charge) => {
                return !charge.deletedAt && !charge.paid;
            });

            this.activeIds = this.activeCharges.map((charge) => {
                return charge.id;
            });

            this.paidCharges = this.charges.selected.filter((charge) => charge.paid);

            this.payorIds = new Set(this.activeCharges.map(charge => charge.payorId));

            this.deletedIds = this.charges.selected.filter((charge) => {
                return charge.deletedAt;
            }).map((charge) => {
                return charge.id;
            });

            this.lateFeeIds = this.charges.selected.filter((charge) => {
                return !charge.deletedAt && !charge.paid && ((charge.lateAfter && charge.lateFee) || charge.lateFees.length > 0);
            }).map((charge) => {
                return charge.id;
            });
            this.earlyDiscountIds = this.charges.selected.filter((charge) => {
                return !charge.deletedAt && !charge.paid && charge.earlyBefore && charge.earlyDiscount;
            }).map((charge) => {
                return charge.id;
            });

            const payors = [];
            this.charges.selected.forEach((charge) => {
                const key = charge.payorType + ' ' + charge.payorId;
                if (payors.indexOf(key) === -1) {
                    payors.push(key);
                }
            });
            this.isSinglePayor = payors.length === 1;
        });
    }

    editCharges() {
        if (this.liveCharges.length === 1) {
            this._chargesService.editCharge$.next(this.liveCharges[0].id);
            return;
        }
        const data: BulkChargeEditorDialogInput = {
            charges: this.liveCharges,
        };
        this._dialog.open(BulkChargeEditorComponent, {
            width: BulkChargeEditorComponent.width,
            data: data,
        }).afterClosed().subscribe({
            next: (result: BulkChargeEditorDialogOutput) => {
                if (result?.success) {
                    this.reloadCharges.emit();
                }
            },
        });
    }

    /**
     * Forgives unpaid balances with different behavior based on payment status.
     *
     * Fully unpaid charges are deleted entirely. Partially paid charges have only the
     * unpaid portion removed, preserving payment history for accurate financial records.
     */
    forgiveCharges() {
        const data: ConfirmWithInputDialogInput = {
            title: 'Forgive Remainder of Invoices',
            content: `Are you sure you want to forgive the remaining balance for (${this.activeCharges.length}) active invoices? Fully unpaid invoices will be deleted. Partially paid invoices will have the unpaid portion deleted.`,
            btnContent: 'Yes, forgive them',
            label: 'Reason for removal',
            inputRequired: false,
        };
        this._dialog.open(ConfirmWithInputDialogComponent, {
            data: data,
            width: ConfirmWithInputDialogComponent.width,
        }).afterClosed().subscribe((result: ConfirmWithInputDialogResult) => {
            if (result.success) {
                this.doShowLoadingOverlay = true;
                this._chargesService.forgiveCharges(this.activeIds, result.value).subscribe(
                        (response: any) => {
                            this.doShowLoadingOverlay = false;
                            this._growler.success('Success', 'Unpaid balances of affected charges have been deleted.');
                            this.reloadCharges.emit();
                        },
                );
            }
        });
    }

    /**
     * Deletes charges with automatic account credit issuance for any payments received.
     *
     * Backend constraint: Deletion is asynchronous and may take several minutes for bulk operations.
     * Account credits are automatically created for any payments made against deleted charges.
     */
    deleteCharges() {
        const data: ConfirmWithInputDialogInput = {
            title: 'Delete Invoice' + ((this.liveCharges.length > 1) ? 's' : ''),
            content: `Are you sure you want to delete the (${this.liveCharges.length}) selected invoices? Payments made against the balance of these invoices will be refunded by issuing the payor an account credit.`,
            btnContent: 'Yes, delete them',
            label: 'Reason for deletion',
            inputRequired: false,
        };
        this._dialog.open(ConfirmWithInputDialogComponent, {
            data: data,
            width: ConfirmWithInputDialogComponent.width,
        }).afterClosed().subscribe((result: ConfirmWithInputDialogResult) => {
            if (result.success) {
                this.doShowLoadingOverlay = true;
                this._chargesService.deleteCharges(null, this.liveCharges.map(c => c.id), result.value).subscribe({
                    next: () => {
                        this.doShowLoadingOverlay = false;
                        this._growler.success('Charges Processing...', 'You have ' + this.liveCharges.length + ' charges being deleted. They should be removed over the next few minutes.');
                        this.reloadCharges.emit();
                    },
                    error: (response) => {
                        this.doShowLoadingOverlay = false;
                        this._growler.error('Unable to delete charges...', this._validationMessageService.extractResponseErrorText(response)
                                || 'We were unable to delete your charges.');
                    },
                });
            }
        });
    }

    removeLateFees() {
        const data: ConfirmActionDialogInput = {
            title: 'Remove Late Fees',
            content: `Are you sure you want to remove late fees from the (${this.lateFeeIds.length}) selected invoices?`,
            confirmButton: 'Yes, remove them',
        };
        this._dialog.open(ConfirmActionDialogComponent, {
            data: data,
            width: ConfirmActionDialogComponent.width,
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                this.doShowLoadingOverlay = true;
                const requestBody: { charge: any, chargeIds: number[] } = {
                    charge: {
                        removeLateFees: true,
                    },
                    chargeIds: this.lateFeeIds,
                };

                this._chargesService.updateCharges(null, requestBody).subscribe({
                    next: () => {
                        this.doShowLoadingOverlay = false;
                        this._growler.success('Success', 'You have removed the late fee(s).');
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this.doShowLoadingOverlay = false;
                        this._growler.error('Oops...', 'Unable to remove late fees.');
                    },
                });
            }
        });
    }

    removeEarlyDiscounts() {
        this._dialog.open(ConfirmActionDialogComponent, {
            data: <ConfirmActionDialogInput>{
                title: 'Remove Early Discounts',
                content: `Are you sure you want to remove the early payment discounts from the (${this.earlyDiscountIds.length}) selected invoices?`,
                confirmButton: 'Yes, remove them',
            },
            width: '500px',
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                this.doShowLoadingOverlay = true;
                const requestBody: { charge: any, chargeIds: number[] } = {
                    charge: {
                        removeEarlyDiscounts: true,
                    },
                    chargeIds: this.earlyDiscountIds,
                };

                this._chargesService.updateCharges(null, requestBody).subscribe({
                    next: (data: any) => {
                        this.doShowLoadingOverlay = false;
                        this._growler.success('Success', 'You have removed early discounts.');
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this.doShowLoadingOverlay = false;
                        this._growler.error('Oops..', 'Unable to remove early discount(s).');
                    },
                });
            }
        });
    }

    recordPayments() {
        this._chargesService.recordPaymentForCharges$.next(this.activeCharges);
    }

    createPaymentPlans() {
        this._dialog.open(PaymentPlanDialogComponent, {
            width: MatDialogSizes.LG,
            data: {
                data: this.activeCharges,
            },
        }).afterClosed().subscribe(result => {
            if (result === 'paymentPlanCreated') {
                this.reloadCharges.emit();
            }
        });
    }

    mailInvoices() {
        this._communicationService.preselectedUnitIds = Array.from(new Set(this.activeCharges.map((c) => c.payorId)));
        const hasFuture = this.activeCharges.filter(c => moment().isBefore(new Date(c.activeAfter)));
        this._router.navigate([
            '/app/mail/send/invoice',
        ], {queryParams: {preselected: 1, future: hasFuture}});
    }

    /**
     * Emails active invoices with autopay-aware filtering based on org preferences.
     *
     * When `sendMonthlyStatementsToAutopayMembers` is false, presents secondary confirmation
     * to either skip autopay-enabled charges or override the preference. Paid invoices are
     * always excluded from email operations.
     */
    emailInvoices() {
        let content = `Are you sure you want to email invoices for the (${this.activeCharges.length}) active selected invoices?`;
        if (this.paidCharges.length > 0) {
            content += ` (${this.paidCharges.length} paid invoices will not be emailed)`;
        }
        const data: ConfirmActionDialogInput = {
            title: 'Email Invoices',
            content: content,
            confirmButton: 'Yes, email invoice',
        };
        this._dialog.open(ConfirmActionDialogComponent, {
            data: data,
            width: ConfirmActionDialogComponent.width,
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                this.doShowLoadingOverlay = true;
                const selectedChargeHasAutopay = this.activeCharges.filter((charge) => {
                    return charge.autopayEnabled === true;
                });
                if (selectedChargeHasAutopay.length > 0 && !this.org.preferences.sendMonthlyStatementsToAutopayMembers) {
                    this._dialog.open(ConfirmActionDialogComponent, {
                        data: <ConfirmActionDialogInput>{
                            title: 'Autopay Detected',
                            content: 'Some of the selected invoices are setup to be autopaid. Your organization is configured to skip invoice emails to autopay users. Would you like to send invoices to these users anyway?',
                            secondaryButton: 'Send To All Users',
                            confirmButton: 'Skip Autopay Users',
                        },
                        width: MatDialogSizes.SM,
                    }).afterClosed().subscribe((res: ConfirmActionDialogResult) => {
                        if (res?.success) {
                            if (!res.secondary) {
                                for (const c of selectedChargeHasAutopay) {
                                    this.charges.deselect(c);
                                }
                            }
                            if (this.charges.selected.length > 0) {
                                this.sendInvoices();
                            } else {
                                this.doShowLoadingOverlay = false;
                                this._growler.oops('No emails to send. All selected invoices are set to be autopaid.');
                            }
                        }
                    });
                } else {
                    this.sendInvoices();
                }
            }
        });
    }

    sendInvoices(overrideAutopayBlock: boolean = false) {
        this._chargesService.sendInvoices(this.activeCharges.map((c) => c.id), overrideAutopayBlock).subscribe({
            next: () => {
                this.doShowLoadingOverlay = false;
                this._growler.success('Success', 'Your invoices are being sent. You will begin to see them in your communication log soon.');
                this.reloadCharges.emit();
            },
            error: () => {
                this.doShowLoadingOverlay = false;
                this._growler.error('Error', 'There was an error sending your invoices. Please try again.');
            },
        });
    }


    reinstateCharges() {
        const data: ConfirmWithInputDialogInput = {
            title: 'Reinstate Invoice' + ((this.deletedIds.length > 1) ? 's' : ''),
            content: `Are you sure you want to reinstate the (${this.deletedIds.length}) selected
        invoice${this.deletedIds.length === 1 ? '' : 's'}?`,
            btnContent: 'Yes, reinstate them',
            label: 'Reason to reinstate',
            inputRequired: false,
        };
        this._dialog.open(ConfirmWithInputDialogComponent, {
            data: data,
            width: ConfirmWithInputDialogComponent.width,
        }).afterClosed().subscribe((result: ConfirmWithInputDialogResult) => {
            if (result.success) {
                this.doShowLoadingOverlay = true;
                this._chargesService.reinstateCharges(this.deletedIds, result.value).subscribe({
                    next: () => {
                        this.doShowLoadingOverlay = false;
                        this._growler.success('Success', 'You have re-instated the charge(s).');
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this.doShowLoadingOverlay = false;
                        this._growler.error('Oops...', 'There was an error reinstating the invoice(s). Please try again.');
                    },
                });
            }
        });
    }

    /**
     * Permanently archives deleted charges with no restoration capability.
     *
     * Archive constraint: Only deleted charges can be archived. This is a permanent operation
     * that removes all traces from the system - no undo or recovery possible.
     */
    archiveCharges() {
        const data: ConfirmActionDialogInput = {
            title: 'Archive Invoice',
            content: `Are you sure you want to archive the selected invoice? Archived invoices will no longer appear anywhere and cannot be restored.`,
            confirmButton: 'Yes, archive it',
        };
        this._dialog.open(ConfirmActionDialogComponent, {
            data: data,
            width: ConfirmActionDialogComponent.width,
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                this.doShowLoadingOverlay = true;
                this._archiveService.archiveRecords(null, 'charge', this.deletedIds).subscribe({
                    next: () => {
                        this.doShowLoadingOverlay = false;
                        this._growler.success('Charges Archived', `Successfully archived ${this.deletedIds.length} charges`);
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this.doShowLoadingOverlay = false;
                        this._growler.error('Error Archiving Charges', `Failed to archive some charges`);
                    },
                });
            }
        });
    }


}
